import React, { useCallback, useEffect, useState } from 'react';
import { Navbar } from '../components/Navbar';
import { AdminLayout } from '../components/AdminSidebar';
import { api, formatApiError } from '../lib/api';
import { toast } from 'sonner';
import { Copy, Plus, X, RotateCcw } from 'lucide-react';

const STATUS_TABS = [
  { id: 'active', label: 'Active' },
  { id: 'disabled', label: 'Disabled' },
  { id: 'exhausted', label: 'Exhausted' },
  { id: 'all', label: 'All' },
];

export default function AdminPromoCodes() {
  const [statusFilter, setStatusFilter] = useState('active');
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newCode, setNewCode] = useState('');
  const [newDiscount, setNewDiscount] = useState(25);
  const [newMaxUses, setNewMaxUses] = useState('');
  const [newExpires, setNewExpires] = useState('30');
  const [newNotes, setNewNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // Random 6-digit code, leading-zero safe so the visual length is uniform.
  const random6 = () => String(Math.floor(100000 + Math.random() * 900000));

  // One-click "test code" preset: 100% off, 1 use, auto-generated 6-digit
  // numeric code, 30-day expiry. Closes the loop on the operator's most
  // common ad-hoc need (QA + customer support gestures).
  const fillTestPreset = () => {
    setNewCode(random6());
    setNewDiscount(100);
    setNewMaxUses('1');
    setNewExpires('30');
    setNewNotes('Single-use 100% test code');
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/promo-codes?status=${statusFilter}`);
      setCodes(data.promo_codes || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        discount_percent: Number(newDiscount),
        notes: newNotes.trim() || null,
      };
      if (newCode.trim()) payload.code = newCode.trim();
      if (newMaxUses) payload.max_uses = Number(newMaxUses);
      if (newExpires) payload.expires_in_days = Number(newExpires);
      const { data } = await api.post('/admin/promo-codes', payload);
      toast.success(`Created ${data.code}`);
      setShowCreate(false);
      setNewCode('');
      setNewDiscount(25);
      setNewMaxUses('');
      setNewExpires('30');
      setNewNotes('');
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const disable = async (code) => {
    if (!window.confirm(`Disable ${code}? People who haven't used it yet won't be able to.`)) return;
    try {
      await api.post(`/admin/promo-codes/${code}/disable`, { reason: 'Disabled by admin' });
      toast.success('Disabled.');
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const reactivate = async (code) => {
    try {
      await api.post(`/admin/promo-codes/${code}/reactivate`);
      toast.success('Reactivated.');
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const copy = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code copied');
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <AdminLayout>
        <header className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--accent))]">Platform admin</p>
            <h1 className="font-serif text-4xl text-[hsl(var(--primary))] mt-2" data-testid="admin-promo-title">
              Promo codes
            </h1>
          </div>
          <button onClick={() => setShowCreate(true)} className="ml-btn-gold" data-testid="admin-promo-new-btn">
            <Plus size={14}/> New code
          </button>
        </header>

        {/* Status filter */}
        <div className="flex flex-wrap gap-2 mb-8">
          {STATUS_TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setStatusFilter(t.id)}
              className={`px-4 py-2 rounded-full text-sm border transition ${
                statusFilter === t.id
                  ? 'bg-[hsl(var(--primary))] text-[hsl(var(--accent))] border-[hsl(var(--primary))]'
                  : 'border-[hsl(var(--primary)/0.18)] text-[hsl(var(--primary)/0.8)] hover:bg-[hsl(var(--primary)/0.04)]'
              }`}
              data-testid={`admin-promo-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Create panel */}
        {showCreate && (
          <form onSubmit={create} className="rounded-2xl border border-[hsl(var(--accent)/0.5)] bg-[hsl(var(--primary)/0.04)] px-7 py-6 mb-8 wizard-readable" data-testid="admin-promo-create-form">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <h2 className="font-serif text-xl text-[hsl(var(--primary))]">New promo code</h2>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={fillTestPreset}
                  className="text-xs uppercase tracking-[0.18em] font-semibold px-3 py-1.5 rounded-full bg-[hsl(var(--accent))] text-[hsl(var(--primary))] hover:opacity-90"
                  data-testid="admin-promo-test-preset"
                  title="Fill the form with: 6-digit code, 100% off, 1 use, 30-day expiry"
                >
                  ⚡ 1-use 100% test code
                </button>
                <button type="button" onClick={() => setShowCreate(false)} className="text-[hsl(var(--primary)/0.6)] hover:text-[hsl(var(--primary))]">
                  <X size={18}/>
                </button>
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <label className="ml-label">Discount %</label>
                <div className="flex gap-2 mt-1 mb-2 flex-wrap">
                  {[25, 50, 75, 100].map((v) => (
                    <button
                      type="button"
                      key={v}
                      onClick={() => setNewDiscount(v)}
                      data-testid={`admin-promo-discount-${v}`}
                      className={`px-3 py-1 rounded text-xs font-bold border ${
                        Number(newDiscount) === v
                          ? 'bg-[hsl(var(--primary))] text-[#F4E5C2] border-[hsl(var(--primary))]'
                          : 'border-[hsl(var(--primary))]/25 text-[hsl(var(--primary))]'
                      }`}
                    >
                      {v}%
                    </button>
                  ))}
                </div>
                <input type="number" min={1} max={100} value={newDiscount} onChange={(e) => setNewDiscount(e.target.value)} className="ml-input" data-testid="admin-promo-discount-input" />
              </div>
              <div>
                <label className="ml-label">Max uses (blank = unlimited)</label>
                <input type="number" min={1} value={newMaxUses} onChange={(e) => setNewMaxUses(e.target.value)} placeholder="—" className="ml-input" data-testid="admin-promo-max-uses" />
              </div>
              <div>
                <label className="ml-label">Expires in (days)</label>
                <input type="number" min={1} max={365} value={newExpires} onChange={(e) => setNewExpires(e.target.value)} className="ml-input" data-testid="admin-promo-expires" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="ml-label">Custom code (optional — blank = auto-generate)</label>
                <div className="flex gap-2">
                  <input
                    value={newCode}
                    onChange={(e) => setNewCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 16))}
                    className="ml-input font-mono tracking-[0.2em]"
                    placeholder="e.g. 823491"
                    data-testid="admin-promo-code-input"
                  />
                  <button
                    type="button"
                    onClick={() => setNewCode(random6())}
                    className="text-xs uppercase tracking-[0.18em] font-semibold px-3 py-1.5 rounded-full border border-[hsl(var(--primary))]/25 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/5 whitespace-nowrap"
                    title="Fill with a random 6-digit code"
                    data-testid="admin-promo-code-random"
                  >
                    ⟲ 6-digit
                  </button>
                </div>
              </div>
              <div>
                <label className="ml-label">Notes (internal)</label>
                <input value={newNotes} onChange={(e) => setNewNotes(e.target.value)} className="ml-input" data-testid="admin-promo-notes" placeholder="What is this code for?" />
              </div>
            </div>
            <div className="mt-5 flex gap-2 flex-wrap">
              <button type="submit" disabled={saving} className="ml-btn-primary" data-testid="admin-promo-submit">
                {saving ? 'Creating…' : 'Create code'}
              </button>
              <button type="button" onClick={() => setShowCreate(false)} className="ml-btn-secondary">Cancel</button>
            </div>
          </form>
        )}

        {/* Codes table */}
        {loading ? (
          <div className="text-[hsl(var(--primary)/0.6)]">Loading…</div>
        ) : codes.length === 0 ? (
          <div className="rounded-2xl border border-[hsl(var(--primary)/0.18)] px-7 py-10 text-center text-[hsl(var(--primary)/0.6)]" data-testid="admin-promo-empty">
            No promo codes match this filter.
          </div>
        ) : (
          <div className="rounded-2xl border border-[hsl(var(--primary)/0.18)] overflow-hidden" data-testid="admin-promo-table">
            <table className="w-full text-sm table-fixed">
              <colgroup>
                <col style={{ width: '17%' }} />
                <col style={{ width: '7%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '24%' }} />
                <col style={{ width: '20%' }} />
              </colgroup>
              <thead className="bg-[hsl(var(--primary)/0.05)] text-[hsl(var(--primary)/0.6)] text-left">
                <tr>
                  <th className="px-3 py-3 font-medium">Code</th>
                  <th className="px-3 py-3 font-medium">Off</th>
                  <th className="px-3 py-3 font-medium">Uses</th>
                  <th className="px-3 py-3 font-medium">Expires</th>
                  <th className="px-3 py-3 font-medium">Kind</th>
                  <th className="px-3 py-3 font-medium">Notes</th>
                  <th className="px-3 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[hsl(var(--primary)/0.08)]">
                {codes.map((c) => (
                  <tr key={c.code} data-testid={`admin-promo-row-${c.code}`}>
                    <td className="px-3 py-3 font-mono tracking-wider text-[hsl(var(--primary))] truncate" title={c.code}>{c.code}</td>
                    <td className="px-3 py-3 font-semibold">{c.discount_percent}%</td>
                    <td className="px-3 py-3 text-xs whitespace-nowrap">{c.times_used || 0}{c.max_uses ? ` / ${c.max_uses}` : ' / ∞'}</td>
                    <td className="px-3 py-3 text-xs opacity-70 whitespace-nowrap">{(c.expires_at || '—').slice(0,10)}</td>
                    <td className="px-3 py-3 text-xs capitalize truncate">{c.kind}</td>
                    <td className="px-3 py-3 text-xs truncate" title={c.notes || c.issued_to_email || ''}>
                      {c.notes || (c.issued_to_email ? `for ${c.issued_to_email}` : '—')}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex gap-2 justify-end items-center flex-nowrap">
                        <button
                          onClick={() => copy(c.code)}
                          className="inline-flex items-center justify-center w-8 h-8 rounded border border-[hsl(var(--primary)/0.25)] text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary)/0.05)] transition-colors shrink-0"
                          data-testid={`admin-promo-copy-${c.code}`}
                          title="Copy code"
                        >
                          <Copy size={13} />
                        </button>
                        {c.status === 'active' ? (
                          <button
                            onClick={() => disable(c.code)}
                            className="inline-flex items-center justify-center h-8 px-3 rounded border border-[#7A3939]/40 bg-[#7A3939]/8 text-[12px] font-semibold text-[#7A3939] hover:bg-[#7A3939]/15 transition-colors shrink-0 whitespace-nowrap"
                            data-testid={`admin-promo-disable-${c.code}`}
                          >
                            Disable
                          </button>
                        ) : (
                          <button
                            onClick={() => reactivate(c.code)}
                            className="inline-flex items-center justify-center gap-1 h-8 px-3 rounded border border-[hsl(var(--primary)/0.25)] text-[12px] font-semibold text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary)/0.05)] transition-colors shrink-0 whitespace-nowrap"
                            data-testid={`admin-promo-reactivate-${c.code}`}
                          >
                            <RotateCcw size={12} /> Reactivate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AdminLayout>
    </div>
  );
}
